import { prefersReducedMotion } from "@js/parts/prefersReducedMotion";

export default function countUp(opts = {}) {
  return {
    useLocale: opts.useLocale ?? true,
    duration: opts.duration || 2,
    startPercentage: opts.startPercentage ?? 0.7,

    init() {
      const element = this.$el;
      const text = element.textContent.trim();

      // Extract number and non-number parts
      const numberMatch = text.match(/[\d,\.]+/);

      if (!numberMatch) {
        return;
      }

      const numberString = numberMatch[0];
      const numberIndex = numberMatch.index;

      // Split text into: prefix + number + suffix
      const prefix = text.substring(0, numberIndex);
      const suffix = text.substring(numberIndex + numberString.length);

      // Remove commas for actual number value
      const targetNumber = parseFloat(numberString.replace(/,/g, ''));

      // Check if it has decimals
      const hasDecimals = numberString.includes('.');
      const decimalPlaces = hasDecimals ? numberString.split('.')[1].length : 0;

      if (prefersReducedMotion()) {
        return;
      }

      // Calculate starting value
      const startValue = targetNumber * this.startPercentage;

      // Set initial display to starting value
      let initialDisplay = hasDecimals
        ? startValue.toFixed(decimalPlaces)
        : Math.floor(startValue);

      const formattedInitial = this.useLocale
        ? parseFloat(initialDisplay).toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces
          })
        : initialDisplay.toString();

      element.textContent = prefix + formattedInitial + suffix;

      // Set up intersection observer for scroll trigger
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            observer.unobserve(element);
            this.animateCounter(element, startValue, targetNumber, prefix, suffix, hasDecimals, decimalPlaces);
          }
        });
      }, {
        rootMargin: '0px 0px -20px 0px' // Similar to "top bottom-=20"
      });

      observer.observe(element);
    },

    animateCounter(element, startValue, targetNumber, prefix, suffix, hasDecimals, decimalPlaces) {
      const counter = { value: startValue };
      const startTime = performance.now();
      const durationMs = this.duration * 1000;

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / durationMs, 1);

        // power2.out easing
        const easeProgress = 1 - Math.pow(1 - progress, 2);

        counter.value = startValue + (targetNumber - startValue) * easeProgress;

        let currentValue = counter.value;

        // Handle decimals
        if (hasDecimals) {
          currentValue = currentValue.toFixed(decimalPlaces);
        } else {
          currentValue = Math.floor(currentValue);
        }

        // Format with commas if useLocale is true
        const formattedValue = this.useLocale
          ? parseFloat(currentValue).toLocaleString(undefined, {
              minimumFractionDigits: decimalPlaces,
              maximumFractionDigits: decimalPlaces
            })
          : currentValue.toString();

        // Update element
        element.textContent = prefix + formattedValue + suffix;

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  };
}
